"use client"

import { useState, useMemo } from "react"
import { usePlayback } from "./usePlayback"
import { PlaybackControls } from "./PlaybackControls"
import type { HashTableStep } from "./types"

interface HashTableVizProps {
  bucketCount: number
  steps: HashTableStep[]
  width?: number
  height?: number
  mode?: "chaining" | "probing"
}

interface Entry {
  key: string
  value: string | number
}

interface ReplayResult {
  buckets: Entry[][]
  highlightedBucket: number | null
  highlightedEntry: { bucket: number; position: number } | null
  probeTrail: number[]
  bucketColors: Map<number, string>
  hashAnnotation: string | null
}

const BUCKET_W = 80
const BUCKET_H = 40
const BUCKET_GAP = 4
const ENTRY_W = 60
const ENTRY_H = 30
const ENTRY_GAP = 8
const ARROW_LEN = 8

const COLOR_DEFAULT = "#ffffff"
const COLOR_AMBER = "#f59e0b"
const COLOR_GREEN = "#22c55e"
const COLOR_RED = "#ef4444"
const COLOR_PROBE = "#fbbf24"

function replaySteps(
  bucketCount: number,
  steps: HashTableStep[],
  upTo: number,
  mode: "chaining" | "probing"
): ReplayResult {
  const buckets: Entry[][] = Array.from({ length: bucketCount }, () => [])
  let highlightedBucket: number | null = null
  let highlightedEntry: { bucket: number; position: number } | null = null
  let probeTrail: number[] = []
  const bucketColors = new Map<number, string>()
  let hashAnnotation: string | null = null

  for (let i = 0; i <= upTo; i++) {
    const step = steps[i]
    if (!step) continue

    switch (step.type) {
      case "hash": {
        // Clear previous highlights for a fresh hash computation
        bucketColors.clear()
        highlightedEntry = null
        probeTrail = []
        if (step.bucketIndex != null) {
          highlightedBucket = step.bucketIndex
          bucketColors.set(step.bucketIndex, COLOR_AMBER)
        }
        if (step.key != null && step.hashValue != null) {
          hashAnnotation = `hash("${step.key}") = ${step.hashValue}`
          if (step.bucketIndex != null && step.bucketIndex !== step.hashValue) {
            hashAnnotation += ` -> bucket ${step.bucketIndex}`
          }
        }
        break
      }

      case "insert": {
        const idx = step.bucketIndex
        if (idx != null && step.key != null) {
          const entry: Entry = {
            key: step.key,
            value: step.value ?? "",
          }
          if (mode === "probing") {
            // In probing mode, each bucket holds at most one entry
            buckets[idx] = [entry]
          } else {
            buckets[idx]!.push(entry)
          }
          bucketColors.clear()
          bucketColors.set(idx, COLOR_GREEN)
          highlightedBucket = idx
          highlightedEntry = { bucket: idx, position: buckets[idx]!.length - 1 }
          probeTrail = []
          hashAnnotation = null
        }
        break
      }

      case "collision": {
        const idx = step.bucketIndex
        if (idx != null) {
          bucketColors.set(idx, COLOR_RED)
          highlightedBucket = idx
        }
        break
      }

      case "probe": {
        const idx = step.probeIndex ?? step.bucketIndex
        if (idx != null) {
          probeTrail.push(idx)
          bucketColors.set(idx, COLOR_PROBE)
          highlightedBucket = idx
        }
        break
      }

      case "find": {
        bucketColors.clear()
        probeTrail = []
        const idx = step.bucketIndex
        if (idx != null) {
          bucketColors.set(idx, COLOR_GREEN)
          highlightedBucket = idx
          // Find the entry position within the bucket
          if (step.key != null) {
            const pos = buckets[idx]!.findIndex((e) => e.key === step.key)
            if (pos >= 0) {
              highlightedEntry = { bucket: idx, position: pos }
            }
          }
        }
        hashAnnotation = null
        break
      }

      case "highlight_bucket": {
        const idx = step.bucketIndex
        if (idx != null) {
          highlightedBucket = idx
          bucketColors.set(idx, COLOR_AMBER)
        }
        break
      }

      case "reset": {
        highlightedBucket = null
        highlightedEntry = null
        probeTrail = []
        bucketColors.clear()
        hashAnnotation = null
        break
      }
    }
  }

  return {
    buckets,
    highlightedBucket,
    highlightedEntry,
    probeTrail,
    bucketColors,
    hashAnnotation,
  }
}

export function HashTableViz({
  bucketCount,
  steps,
  width = 600,
  height = 400,
  mode = "chaining",
}: HashTableVizProps) {
  const {
    stepIndex,
    isPlaying,
    atStart,
    atEnd,
    stepForward,
    stepBack,
    play,
    pause,
    reset,
  } = usePlayback(steps.length)

  const {
    buckets,
    highlightedBucket,
    highlightedEntry,
    probeTrail,
    bucketColors,
    hashAnnotation,
  } = useMemo(
    () =>
      stepIndex < 0
        ? {
            buckets: Array.from({ length: bucketCount }, () => []) as Entry[][],
            highlightedBucket: null,
            highlightedEntry: null,
            probeTrail: [] as number[],
            bucketColors: new Map<number, string>(),
            hashAnnotation: null,
          }
        : replaySteps(bucketCount, steps, stepIndex, mode),
    [bucketCount, steps, stepIndex, mode]
  )

  const currentStep = stepIndex >= 0 ? steps[stepIndex] : undefined

  // Vertical centering offset
  const totalBucketHeight =
    bucketCount * BUCKET_H + (bucketCount - 1) * BUCKET_GAP
  const originY = Math.max(20, (height - totalBucketHeight) / 2)
  const originX = 20

  return (
    <div className="flex flex-col gap-3">
      {/* Hash computation annotation */}
      <div
        className="h-8 flex items-center justify-center text-sm font-mono text-amber-600"
        style={{ transition: "opacity 400ms ease", opacity: hashAnnotation ? 1 : 0 }}
      >
        {hashAnnotation ?? "\u00a0"}
      </div>

      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="rounded-lg border border-border bg-background"
      >
        <g transform={`translate(${originX}, ${originY})`}>
          {Array.from({ length: bucketCount }, (_, bIdx) => {
            const bucketY = bIdx * (BUCKET_H + BUCKET_GAP)
            const bucketFill = bucketColors.get(bIdx) ?? COLOR_DEFAULT
            const isHighlighted = bucketColors.has(bIdx)
            const entries = buckets[bIdx]

            return (
              <g key={bIdx}>
                {/* Bucket rect */}
                <rect
                  x={0}
                  y={bucketY}
                  width={BUCKET_W}
                  height={BUCKET_H}
                  rx={4}
                  ry={4}
                  stroke="#d1d5db"
                  strokeWidth={1}
                  style={{
                    fill: bucketFill,
                    transition: "all 400ms ease",
                  }}
                />

                {/* Bucket index label */}
                <text
                  x={BUCKET_W / 2}
                  y={bucketY + BUCKET_H / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={14}
                  fontWeight={600}
                  fontFamily="ui-monospace, monospace"
                  fill={isHighlighted ? "#ffffff" : "#374151"}
                  style={{ transition: "fill 400ms ease" }}
                >
                  {bIdx}
                </text>

                {/* Chaining mode: entries extend right */}
                {mode === "chaining" &&
                  entries?.map((entry, eIdx) => {
                    const entryX =
                      BUCKET_W + ENTRY_GAP + eIdx * (ENTRY_W + ARROW_LEN + ENTRY_GAP)
                    const entryY = bucketY + (BUCKET_H - ENTRY_H) / 2
                    const isEntryHighlighted =
                      highlightedEntry?.bucket === bIdx &&
                      highlightedEntry?.position === eIdx
                    const entryFill = isEntryHighlighted ? COLOR_GREEN : "#f3f4f6"

                    return (
                      <g key={`${bIdx}-${eIdx}`}>
                        {/* Arrow from previous element */}
                        <line
                          x1={
                            eIdx === 0
                              ? BUCKET_W
                              : BUCKET_W +
                                ENTRY_GAP +
                                (eIdx - 1) * (ENTRY_W + ARROW_LEN + ENTRY_GAP) +
                                ENTRY_W
                          }
                          y1={bucketY + BUCKET_H / 2}
                          x2={entryX}
                          y2={bucketY + BUCKET_H / 2}
                          stroke="#9ca3af"
                          strokeWidth={1.5}
                          markerEnd="url(#arrowhead)"
                          style={{ transition: "all 400ms ease" }}
                        />

                        {/* Entry rect */}
                        <rect
                          x={entryX}
                          y={entryY}
                          width={ENTRY_W}
                          height={ENTRY_H}
                          rx={3}
                          ry={3}
                          stroke="#d1d5db"
                          strokeWidth={1}
                          style={{
                            fill: entryFill,
                            transition: "all 400ms ease",
                          }}
                        />

                        {/* Entry text */}
                        <text
                          x={entryX + ENTRY_W / 2}
                          y={entryY + ENTRY_H / 2}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontSize={11}
                          fontFamily="ui-monospace, monospace"
                          fill={isEntryHighlighted ? "#ffffff" : "#1f2937"}
                          style={{ transition: "fill 400ms ease" }}
                        >
                          {entry.key}:{entry.value}
                        </text>
                      </g>
                    )
                  })}

                {/* Probing mode: show entry inside the bucket cell */}
                {mode === "probing" && entries && entries.length > 0 && (
                  <text
                    x={BUCKET_W + 12}
                    y={bucketY + BUCKET_H / 2}
                    textAnchor="start"
                    dominantBaseline="central"
                    fontSize={12}
                    fontFamily="ui-monospace, monospace"
                    fill="#374151"
                    style={{ transition: "opacity 400ms ease" }}
                  >
                    {entries[0]!.key}:{entries[0]!.value}
                  </text>
                )}
              </g>
            )
          })}

          {/* Probe trail arrows (probing mode) */}
          {mode === "probing" &&
            probeTrail.length > 1 &&
            probeTrail.slice(0, -1).map((fromIdx, i) => {
              const toIdx = probeTrail[i + 1]!
              const fromY = fromIdx * (BUCKET_H + BUCKET_GAP) + BUCKET_H / 2
              const toY = toIdx * (BUCKET_H + BUCKET_GAP) + BUCKET_H / 2
              const arrowX = BUCKET_W + 40

              return (
                <g key={`probe-${i}`}>
                  <line
                    x1={arrowX}
                    y1={fromY}
                    x2={arrowX}
                    y2={toY}
                    stroke={COLOR_AMBER}
                    strokeWidth={2}
                    strokeDasharray="4 3"
                    markerEnd="url(#probeArrow)"
                    style={{ transition: "all 400ms ease" }}
                  />
                </g>
              )
            })}
        </g>

        {/* SVG defs for arrowheads */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="8"
            markerHeight="6"
            refX="8"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 8 3, 0 6" fill="#9ca3af" />
          </marker>
          <marker
            id="probeArrow"
            markerWidth="8"
            markerHeight="6"
            refX="8"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 8 3, 0 6" fill={COLOR_AMBER} />
          </marker>
        </defs>
      </svg>

      <PlaybackControls
        message={currentStep?.message ?? ""}
        isPlaying={isPlaying}
        atStart={atStart}
        atEnd={atEnd}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        onStepBack={stepBack}
        onStepForward={stepForward}
        onPlay={play}
        onPause={pause}
        onReset={reset}
      />
    </div>
  )
}
